import { Typography, Grid, Button, Box } from '@mui/material'
import { useFormik } from 'formik'
import { updateProfile } from 'services/userServices'
import { ColStack } from 'components/atoms/container/InCardStack'
import { useEffect, useState } from 'react'
import NavigateButtons from 'components/molecules/onboarding/NavigateButtons'
import { ToggleOptions } from 'components/molecules/onboarding/PathOnboardingMolecules'
import OverlayLoading from 'components/atoms/OverlayLoading'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import { getLocalUser } from 'utils/localStorage'
import { useTranslation, Trans } from 'react-i18next'
import { getGoals } from 'utils/getSupportingInfo'
import PathCard from 'components/molecules/home/PathCard'
import colors from 'styles/colors'
import CardCarousel from 'components/molecules/CardCarousel'

const StepTwo = ({
  subStep,
  setSelectedGoal,
  onNextCallback = () => {},
  onBackCallback = () => {}
}) => {
  const { t } = useTranslation()
  const userInfo = getLocalUser()
  const [requestError, setRequestError] = useState(undefined)
  const [goals, setGoals] = useState([])
  const [skills, setSkills] = useState([])
  const [selectedSkills, setSelectedSkills] = useState(
    userInfo?.onboardingAnswers?.skills || []
  )
  const [loading, setLoading] = useState(false)
  const isCohortUser = userInfo?.isCohortUser || false
  const hasCurrentGoal = Boolean(userInfo?.userStates?.currentGoalId)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const goalsRes = await getGoals(true)
      let filteredGoals = goalsRes.filter(goal => !goal.isLocked)
      if (hasCurrentGoal) {
        const assignedGoal = filteredGoals.find(
          goal => goal.id === userInfo?.userStates?.currentGoalId
        )

        if (assignedGoal) {
          filteredGoals = [assignedGoal]
        }
      }
      setGoals(filteredGoals)

      const virtues = [
        ...new Map(
          filteredGoals.flatMap(goal => goal.virtues).map(v => [v.name, v])
        ).values()
      ].sort((a, b) => a.name?.localeCompare(b.name))
      setSkills(virtues)
      setLoading(false)
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    initialValues: {
      onboardingAnswers: userInfo.onboardingAnswers || {}
    },
    onSubmit: async values => {
      setRequestError(false)
      setLoading(true)
      const data = new FormData()
      data.append('onboardingAnswers', JSON.stringify(values.onboardingAnswers))
      const res = await updateProfile(data)
      if (res.status === 200) {
        onNextCallback()
      } else {
        setRequestError(true)
      }
      setLoading(false)
    }
  })

  const onNext = () => {
    if (hasCurrentGoal && goals[0]) {
      setSelectedGoal(goals[0])
      return
    }
    if (subStep === 0 && formik.dirty) {
      formik.submitForm()
    } else {
      onNextCallback()
    }
  }

  const StepHeader = ({ headingKey, bodyKey }) => {
    return (
      <ColStack spacing={4}>
        <Typography variant='h2'>
          <Trans i18nKey={headingKey} />
        </Typography>
        <Typography color='textSecondary'>
          <Trans i18nKey={bodyKey} />
        </Typography>
      </ColStack>
    )
  }

  return (
    <>
      <OverlayLoading open={loading} />

      {subStep === 0 && !isCohortUser && !hasCurrentGoal ? (
        <>
          <StepHeader
            headingKey={'onboarding.whatYouLikeToFocus'}
            bodyKey={'onboarding.pickSkills'}
          />

          <ToggleOptions
            options={skills.map(s => ({
              label: s.name,
              value: s.id,
              disabled:
                !selectedSkills.includes(s.id) && selectedSkills.length >= 5
            }))}
            value={selectedSkills}
            onChange={newValue => {
              setSelectedSkills(newValue)
              formik.setFieldValue('onboardingAnswers', {
                ...formik.values.onboardingAnswers,
                skills: newValue
              })
            }}
            multiple={true}
          />
        </>
      ) : (
        <>
          {isCohortUser || hasCurrentGoal ? (
            <StepHeader
              headingKey={'onboarding.yourLeadershipJourney'}
              bodyKey={'onboarding.beforeDivingIntoContent'}
            />
          ) : (
            <ColStack spacing={12}>
              <StepHeader
                headingKey={'onboarding.yourRecommendedPaths'}
                bodyKey={'onboarding.onYourSelectedSkills'}
              />

              <ToggleOptions
                disabled
                options={skills
                  .filter(s => selectedSkills.includes(s.id))
                  .map(s => ({
                    label: s.name,
                    value: s.id
                  }))}
                value={selectedSkills}
                multiple={true}
              />

              <Button
                onClick={onBackCallback}
                variant='outlined'
                size='small'
                color='primary'>
                {t('onboarding.reselectSkills')}
              </Button>
            </ColStack>
          )}
          {goals?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                width: '100%'
              }}>
              <CardCarousel
                containerSx={{
                  display: 'inline-flex',
                  borderRadius: 3,
                  backgroundColor: colors.MutedTeal300,
                  paddingY: 6,
                  paddingX: 2,
                  boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)'
                }}
                customHeight={'100%'}
                customWidth={308}
                paddingX={6}
                showNavigation={false}
                cards={goals
                  .filter(goal =>
                    isCohortUser || hasCurrentGoal
                      ? true
                      : goal.virtues.some(v => selectedSkills.includes(v.id))
                  )
                  .map((goal, index) => (
                    <PathCard
                      key={`goal-${index}`}
                      goal={goal}
                      isLibrary={true}
                      forOnboarding={true}
                      sx={{ width: 308, boxSizing: 'border-box' }}
                      onboardingCallback={selectedGoal => {
                        setSelectedGoal(selectedGoal)
                      }}
                    />
                  ))}
              />
            </Box>
          )}
        </>
      )}

      <Grid position='sticky'>
        <NavigateButtons
          onNext={onNext}
          onBack={onBackCallback}
          nextProps={{
            disabled:
              !hasCurrentGoal &&
              subStep === 0 &&
              (selectedSkills.length < 3 || selectedSkills.length > 5)
          }}
          showNext={(subStep === 0 && !isCohortUser) || hasCurrentGoal}
        />
      </Grid>

      {requestError && <RequestRetryAlert />}
    </>
  )
}

export default StepTwo
